import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  ChevronLeft, ChevronRight, UserCheck, CalendarX, Users, AlertTriangle,
  ArrowLeft, Download, Search, CalendarDays,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import Shimmer from "../Common/Shimmer";
import CommonToaster from "../Common/CommonToaster";
import { EmptyState } from "../ui";
import "./attendance.scss";

/**
 * BatchAttendanceRegister — book-style attendance register (matrix) for a batch.
 *
 * Denominator comes from the backend "expected class days" engine (batch
 * schedule ∩ working days − holidays), so the % reflects classes the batch was
 * actually meant to hold — not just sessions that happened.
 *
 * Views:
 *   • Week     — full Mon–Sun grid (book style): every weekday is a column;
 *                non-class days are greyed and holidays struck out.
 *   • Month    — the days the batch met that month (class-days matrix).
 *   • 3 Months — same, across a quarter (for trend/report; scrolls).
 *
 * Class-day cells: present | partial | absent | excused | not_marked | upcoming.
 * Used standalone (Batch Register page, with header/back) and embedded in
 * the admin batch page's Attendance tab (no header).
 *
 * Props:
 *   batchId      (required)
 *   defaultView  "week" | "month" | "quarter"  — initial view (default "month")
 *   showHeader   show the back arrow + batch-name header (default false)
 *   batchName    label for the header
 *   onBack       () => void — back-arrow handler
 */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DOW2 = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const DOW_FULL = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const VIEWS = [
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
  { key: "quarter", label: "3 Months" },
];

const CELL = {
  present: { ch: "P", cls: "att-cell--present", label: "Present" },
  partial: { ch: "P", cls: "att-cell--partial", label: "Partial" },
  absent: { ch: "A", cls: "att-cell--absent", label: "Absent" },
  excused: { ch: "L", cls: "att-cell--excused", label: "Excused / Leave" },
  not_marked: { ch: "·", cls: "att-cell--notmarked", label: "Not marked" },
  upcoming: { ch: "", cls: "att-cell--upcoming", label: "Upcoming" },
};

const pctVariant = (p) => (p >= 75 ? "att-pct--good" : p >= 50 ? "att-pct--warn" : "att-pct--bad");

// 'YYYY-MM-DD' → local Date (component-built, timezone-stable)
const toDate = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const ymd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
// Monday-start week containing `d`.
const startOfWeekMon = (d) => {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = x.getDay(); // 0=Sun … 6=Sat
  x.setDate(x.getDate() + (dow === 0 ? -6 : 1 - dow));
  return x;
};

// from/to (YYYY-MM-DD) for a given view + anchor date.
const windowFor = (view, anchor) => {
  if (view === "week") {
    const s = startOfWeekMon(anchor);
    const e = new Date(s);
    e.setDate(s.getDate() + 6);
    return { from: ymd(s), to: ymd(e) };
  }
  if (view === "quarter") {
    const s = new Date(anchor.getFullYear(), anchor.getMonth() - 2, 1);
    const e = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    return { from: ymd(s), to: ymd(e) };
  }
  const s = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const e = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  return { from: ymd(s), to: ymd(e) };
};

const meetingDaysLabel = (arr) =>
  (arr || []).slice().sort((a, b) => a - b).map((d) => DOW_FULL[d]).join(", ");

const BatchAttendanceRegister = ({
  batchId,
  defaultView = "month",
  showHeader = false,
  batchName,
  onBack,
}) => {
  const [view, setView] = useState(defaultView);
  const [anchor, setAnchor] = useState(() => new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [belowOnly, setBelowOnly] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const range = useMemo(() => windowFor(view, anchor), [view, anchor]);

  const fetchRegister = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await apiClient.get(
        `/attendance/batch/${batchId}/register?from=${range.from}&to=${range.to}`
      );
      setData(d);
    } catch {
      setData(null);
    }
    setLoading(false);
  }, [batchId, range.from, range.to]);

  useEffect(() => { fetchRegister(); }, [fetchRegister]);

  const step = (dir) => {
    setAnchor((a) => {
      if (view === "week") { const x = new Date(a); x.setDate(a.getDate() + dir * 7); return x; }
      if (view === "quarter") return new Date(a.getFullYear(), a.getMonth() + dir * 3, 1);
      return new Date(a.getFullYear(), a.getMonth() + dir, 1);
    });
  };

  const periodLabel = useMemo(() => {
    if (view === "week") {
      const s = startOfWeekMon(anchor);
      const e = new Date(s); e.setDate(s.getDate() + 6);
      if (s.getMonth() === e.getMonth()) {
        return `${s.getDate()}–${e.getDate()} ${MONTHS[s.getMonth()]} ${s.getFullYear()}`;
      }
      if (s.getFullYear() === e.getFullYear()) {
        return `${s.getDate()} ${MONTHS[s.getMonth()]} – ${e.getDate()} ${MONTHS[e.getMonth()]} ${s.getFullYear()}`;
      }
      return `${s.getDate()} ${MONTHS[s.getMonth()]} ${s.getFullYear()} – ${e.getDate()} ${MONTHS[e.getMonth()]} ${e.getFullYear()}`;
    }
    if (view === "quarter") {
      const s = new Date(anchor.getFullYear(), anchor.getMonth() - 2, 1);
      return s.getFullYear() === anchor.getFullYear()
        ? `${MONTHS[s.getMonth()]} – ${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`
        : `${MONTHS[s.getMonth()]} ${s.getFullYear()} – ${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`;
    }
    return `${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`;
  }, [view, anchor]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ batchId, startDate: range.from, endDate: range.to });
      const res = await apiClient.get(`/attendance/export?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `attendance_${batchName || "batch"}_${range.from}_${range.to}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setToast({ show: true, message: "Export failed. Please try again.", type: "error" });
    }
    setExporting(false);
  };

  const days = useMemo(() => data?.days || [], [data]);
  const allStudents = useMemo(() => data?.students || [], [data]);
  const summary = data?.summary || {};
  const minPct = data?.minAttendancePct ?? 75;

  // Week → full grid (every day); Month/Quarter → only the days the batch met.
  const columns = useMemo(
    () => (view === "week" ? days : days.filter((d) => d.type === "class")),
    [days, view]
  );

  const students = useMemo(() => {
    let rows = allStudents;
    const q = search.trim().toLowerCase();
    if (q) rows = rows.filter((s) => (s.name || "").toLowerCase().includes(q));
    if (belowOnly) rows = rows.filter((s) => s.percentage != null && s.percentage < minPct);
    return rows;
  }, [allStudents, search, belowOnly, minPct]);

  const renderCell = (col, stu) => {
    if (col.type === "class") {
      const cfg = CELL[stu.cells?.[col.date]] || CELL.not_marked;
      return (
        <td key={col.date} className={`att-cell ${cfg.cls}`} title={`${col.date} — ${cfg.label}`}>
          {cfg.ch}
        </td>
      );
    }
    if (col.type === "holiday") {
      return <td key={col.date} className="att-cell att-cell--holiday" title={`${col.date} — Holiday / closed`}>✕</td>;
    }
    if (col.type === "outofrange") {
      return <td key={col.date} className="att-cell att-cell--outofrange" title={`${col.date} — Outside batch dates`} />;
    }
    return <td key={col.date} className="att-cell att-cell--off" title={`${col.date} — No class scheduled`}>·</td>;
  };

  return (
    <div className="att-register">
      {showHeader && (
        <div className="att-register__head">
          {onBack && (
            <button type="button" className="att-register__back" onClick={onBack} aria-label="Back to batches">
              <ArrowLeft size={18} />
            </button>
          )}
          <div className="att-register__head-text">
            <h2 className="att-register__title">{batchName || data?.batch?.name || "Attendance Register"}</h2>
            {(data?.batch?.meetingDays?.length > 0) && (
              <span className="att-register__subtitle">
                <CalendarDays size={13} /> Meets {meetingDaysLabel(data.batch.meetingDays)}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Toolbar: view switch · period nav · filters · export */}
      <div className="att-register__toolbar">
        <div className="attendance-tabs att-register__views" role="tablist" aria-label="Register view">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              role="tab"
              aria-selected={view === v.key}
              className={`attendance-tab ${view === v.key ? "attendance-tab--active" : ""}`}
              onClick={() => setView(v.key)}
            >
              {v.label}
            </button>
          ))}
        </div>

        <div className="att-register__nav">
          <button type="button" className="att-register__navbtn" onClick={() => step(-1)} aria-label="Previous period">
            <ChevronLeft size={16} />
          </button>
          <span className="att-register__period">{periodLabel}</span>
          <button type="button" className="att-register__navbtn" onClick={() => step(1)} aria-label="Next period">
            <ChevronRight size={16} />
          </button>
          <button type="button" className="att-register__today" onClick={() => setAnchor(new Date())}>
            Today
          </button>
        </div>

        <div className="att-register__tools">
          <div className="att-register__search">
            <Search size={14} />
            <input
              type="text"
              placeholder="Find student…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Find student"
            />
          </div>
          <label className="att-register__below">
            <input type="checkbox" checked={belowOnly} onChange={(e) => setBelowOnly(e.target.checked)} />
            Below {minPct}%
          </label>
          <button type="button" className="att-register__export" onClick={handleExport} disabled={exporting}>
            <Download size={14} /> {exporting ? "Exporting…" : "Export"}
          </button>
        </div>
      </div>

      {loading ? (
        <Shimmer height={40} count={5} />
      ) : allStudents.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No active students"
          description="Add students to this batch to start tracking attendance."
        />
      ) : columns.length === 0 ? (
        <EmptyState
          icon={UserCheck}
          title="No classes in this period"
          description="This batch has no scheduled class days in the selected range. Try another period or set the batch's meeting days."
        />
      ) : (
        <>
          {/* Summary chips */}
          <div className="att-register__chips">
            <div className="att-chip"><Users size={14} /> {summary.totalStudents || 0} students</div>
            <div className="att-chip"><UserCheck size={14} /> {summary.held || 0} classes held</div>
            {summary.notMarked > 0 && (
              <div className="att-chip att-chip--warn">
                <CalendarX size={14} /> {summary.notMarked} not marked
              </div>
            )}
            <div className="att-chip">
              Avg {summary.avgPercentage == null ? "—" : `${summary.avgPercentage}%`}
            </div>
            {summary.belowThreshold > 0 && (
              <div className="att-chip att-chip--bad">
                <AlertTriangle size={14} /> {summary.belowThreshold} below {minPct}%
              </div>
            )}
          </div>

          {/* Register matrix */}
          {students.length === 0 ? (
            <EmptyState
              icon={Search}
              title="No students match your filters"
              description="Clear the search or the below-threshold filter to see everyone."
            />
          ) : (
            <div className="att-register__scroll">
              <table className="att-matrix">
                <thead>
                  <tr>
                    <th className="att-matrix__namecol">Student</th>
                    {columns.map((col) => {
                      const dt = toDate(col.date);
                      return (
                        <th
                          key={col.date}
                          className={`att-matrix__daycol att-matrix__daycol--${col.type}`}
                          title={col.date}
                        >
                          <span className="att-matrix__dow">{DOW2[dt.getDay()]}</span>
                          <span className="att-matrix__dom">{dt.getDate()}</span>
                        </th>
                      );
                    })}
                    <th className="att-matrix__pctcol">%</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((s) => (
                    <tr key={s.studentId}>
                      <td className="att-matrix__namecol" title={s.name}>{s.name}</td>
                      {columns.map((col) => renderCell(col, s))}
                      <td className="att-matrix__pctcol">
                        {s.percentage == null ? (
                          <span className="att-pct att-pct--none">—</span>
                        ) : (
                          <span className={`att-pct ${pctVariant(s.percentage)}`}>{s.percentage}%</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Legend */}
          <div className="att-legend">
            <span><i className="att-dot att-cell--present" /> Present</span>
            <span><i className="att-dot att-cell--absent" /> Absent</span>
            <span><i className="att-dot att-cell--partial" /> Partial</span>
            <span><i className="att-dot att-cell--excused" /> Leave</span>
            <span><i className="att-dot att-cell--notmarked" /> Not marked</span>
            {view === "week" && (
              <>
                <span><i className="att-dot att-cell--off" /> No class</span>
                <span><i className="att-dot att-cell--holiday" /> Holiday</span>
              </>
            )}
          </div>
        </>
      )}

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default BatchAttendanceRegister;
